import { useCallback, useEffect } from 'react';
import { getKcLang } from 'src/keycloak';
import {
  setActionsData,
  setExternalApiCredentials,
  setIcons,
  setOrgLogo,
  setDefaultIcons,
  setUserAppPermissions,
  setModelSettings,
  setUserFeaturePermissions,
  setUserDataLibraryViewPreference,
  setUserDocAnalystOnboarding,
  setUserFavourites,
  setUserIdentityProviders,
  setUserResourceProvider,
  updateChatDefaultModel,
  updateConfiguredModels,
  updateDataAnalystQueryCount,
  updateDocAnalystAnalysisCount,
  updateLookups,
  updateModelTiersRateLimit,
  updateSearchPlusCount,
  updateSmartMeetCount,
  updateUserContactInfo,
  updateUserCustomInstructions,
  updateUserLanguage,
  updateUserOnboarding
} from 'src/redux/slices/data';
import {
  useLazyGetActionsQuery,
  useLazyGetConfiguredAndDefaultModelsQuery,
  useLazyGetFavouriteQuery,
  useLazyGetIconsQuery,
  useLazyGetIdentityProviderQuery,
  useLazyGetLicenseLimitsQuery,
  useLazyGetLookupsQuery,
  useLazyGetModelsRateLimitTiersQuery,
  useLazyGetPermissionsQuery,
  useLazyGetResourceProviderQuery,
  useLazyGetServiceClientQuery,
  useLazyGetUserPreferencesQuery
} from 'src/services/api';
import { ActionsApiResponse } from 'src/types/actions';
import { DataApiInputParams } from 'src/types/api';
import { IAiModel, IModelTierConfig, StringKeys } from 'src/types/base';
import { LOOKUP, PERMISSION_TYPE, VIEW_TYPE } from 'src/types/enum';
import {
  CustomInstructions,
  IGetIdentityProviderResponse
} from 'src/types/settings';
import { ErrorContext } from 'src/utils/errorMappings';
import {
  MODEL_SETTINGS_PREFERENCE_KEY,
  parseModelSettings
} from 'src/utils/model-settings';
import { getBrowserLanguage, getModelData } from 'src/utils/utils';

import initializei18n from '../i18n/i18n';
import { RootState, useDispatch, useSelector } from '../redux/store';
import useLazyQuery from './useLazyQuery';

const FILE_TAGS_POLLING_TIME = 1000 * 60 * 2;
function useGlobalData() {
  const dispatch = useDispatch();
  const { chatDefaultModel } = useSelector((state) => state.data);
  const { tenant_info } = useSelector((state: RootState) => state.library);
  // const [fetchTags, { data: tagsList }] = useLazyQuery<
  //   DataApiInputParams,
  //   StringKeys
  // >(useLazyGetTagsQuery);
  // const [fetchFilesTags, { data: allFileTags }] = useLazyQuery<
  //   DataApiInputParams,
  //   StringKeys
  // >(useLazyGetFilesTagsQuery, {
  //   pollingInterval: FILE_TAGS_POLLING_TIME
  // });
  const [fetchLookups, { data: lookups }] = useLazyQuery<
    DataApiInputParams,
    StringKeys
  >({ api: useLazyGetLookupsQuery });
  const [fetchUserAppPermissions, { data: permissionsResponse }] =
    useLazyQuery<
      DataApiInputParams,
      { app_perms: StringKeys; feature_perms: StringKeys }
    >({ api: useLazyGetPermissionsQuery });
  const [getUserPreferences, { data: userPreferences }] = useLazyQuery<
    DataApiInputParams,
    StringKeys
  >({
    api: useLazyGetUserPreferencesQuery,
    errorContext: ErrorContext.GENERAL
  });
  const [getLicenseLimits, { data: licenseLimits }] = useLazyQuery<
    DataApiInputParams,
    StringKeys
  >({
    api: useLazyGetLicenseLimitsQuery,
    errorContext: ErrorContext.GENERAL
  });
  const [fetchResourceProviders, { data: userProviders }] = useLazyQuery<
    void,
    unknown
  >({
    api: useLazyGetResourceProviderQuery,
    errorContext: ErrorContext.GENERAL
  });
  const [fetchIdentityProviders, { data: identityProviders }] = useLazyQuery<
    void,
    IGetIdentityProviderResponse
  >({
    api: useLazyGetIdentityProviderQuery,
    errorContext: ErrorContext.GENERAL
  });
  const [fetchFavourites, { data: favourites }] = useLazyQuery({
    api: useLazyGetFavouriteQuery
  });
  const [getConfiguredAndDefaultModels, { data: configuredModels }] =
    useLazyQuery({
      api: useLazyGetConfiguredAndDefaultModelsQuery
    });

  const [fetchModelTiersDetails, { data: modelTiers }] = useLazyQuery({
    api: useLazyGetModelsRateLimitTiersQuery
  });

  const [fetchIcons, { data: iconData }] = useLazyQuery({
    api: useLazyGetIconsQuery,
    errorContext: ErrorContext.ICONS
  });
  // TODO commented out till finalized design is not provided
  const [fetchExternalApiCredentials, { data: externalApiCredentials }] =
    useLazyQuery<DataApiInputParams, StringKeys>({
      api: useLazyGetServiceClientQuery
    });

  const [fetchActions, { isFetching, data: actions }] = useLazyQuery<
    DataApiInputParams,
    ActionsApiResponse
  >({
    api: useLazyGetActionsQuery,
    errorContext: ErrorContext.GENERAL
  });
  const initialize = useCallback(() => {
    getUserPreferences({
      params: {
        params: {
          settingsList: [
            'lang, onboarded',
            'is_custom_instructions_enabled',
            'user_personalization',
            'response_customization'
          ]
        }
      }
    });
    getLicenseLimits({
      params: {
        params: {
          settingsList: [
            'doc_analyst_analysis_count',
            'data_analyst_query_count',
            'search_plus_count',
            'consumed_smart_meet_seconds'
          ]
        }
      }
    });
    fetchUserAppPermissions({
      params: {
        params: {
          ptype: PERMISSION_TYPE.USER_APP_PERMISSIONS
        }
      }
    });
    fetchLookups({
      params: {
        params: { lookup_types: [...Object.values(LOOKUP)].toString() }
      }
    });
    fetchIcons({ params: undefined });
    fetchResourceProviders({ params: undefined });
    fetchIdentityProviders({ params: undefined });
    fetchFavourites({ params: undefined });
    fetchModelTiersDetails({ params: undefined });
    getConfiguredAndDefaultModels({ params: undefined });
    fetchActions({ params: undefined });

    // TODO commented out till finalized design is not provided
    fetchExternalApiCredentials({ params: undefined });
  }, []);

  useEffect(() => {
    if (lookups) {
      dispatch(updateLookups(lookups));
    }
  }, [lookups]);

  useEffect(() => {
    if (permissionsResponse) {
      dispatch(setUserAppPermissions(permissionsResponse.app_perms));
      dispatch(setUserFeaturePermissions(permissionsResponse.feature_perms));
    }
  }, [permissionsResponse]);

  useEffect(() => {
    if (favourites?.data) {
      dispatch(setUserFavourites(favourites?.data));
    }
  }, [favourites]);

  useEffect(() => {
    if (userProviders) {
      dispatch(
        setUserResourceProvider(
          (
            userProviders as {
              data: {
                resource_provider: StringKeys;
                resource_provider_settings: StringKeys[];
              }[];
            }
          )?.data?.map((item) => ({
            ...item.resource_provider,
            ...item.resource_provider_settings.reduce(
              (acc, { key, value }) => ({ ...acc, [key]: value }),
              {}
            )
          }))
        )
      );
    }
  }, [userProviders]);

  useEffect(() => {
    if (identityProviders) {
      dispatch(setUserIdentityProviders(identityProviders.data));
    }
  }, [identityProviders]);

  useEffect(() => {
    if (iconData) {
      dispatch(setIcons(iconData?.icons || []));
      dispatch(setDefaultIcons(iconData?.default_icons || []));
      dispatch(setOrgLogo(iconData?.org_logos || {}));
    }
  }, [iconData]);

  useEffect(() => {
    if (userPreferences?.data) {
      const getPreference = (key: string) =>
        userPreferences?.data?.find((item) => item.name === key)?.value;

      let lang = getPreference('lang') || getKcLang() || getBrowserLanguage();
      let isUserOnboarded = getPreference('onboarded');
      isUserOnboarded = (isUserOnboarded || '') === 'true' ? true : false;
      let isCompanyInfo = getPreference('is_contact_info_filled');
      isCompanyInfo = (isCompanyInfo || '') === 'true' ? true : false;
      let userDataLibraryViewPreference =
        getPreference('user_dl_view_preference') || VIEW_TYPE.GRID;
      const customInstructionsObj: CustomInstructions = {
        isCustomInstructionsEnabled:
          getPreference('is_custom_instructions_enabled') === 'true'
            ? true
            : false,
        userPersonalization: getPreference('user_personalization') || '',
        responseCustomization: getPreference('response_customization') || ''
      };
      let isDocAnalystOnboarded =
        (getPreference('is_doc_analyst_onboarded') || '') === 'true'
          ? true
          : false;

      const modelSettings = parseModelSettings(
        getPreference(MODEL_SETTINGS_PREFERENCE_KEY)
      );

      if (lang) {
        initializei18n.changeLanguage(lang);
      }
      dispatch(updateUserLanguage(lang));
      dispatch(updateUserOnboarding(isUserOnboarded));
      dispatch(updateUserCustomInstructions(customInstructionsObj));
      dispatch(setUserDataLibraryViewPreference(userDataLibraryViewPreference));
      dispatch(setUserDocAnalystOnboarding(isDocAnalystOnboarded));
      dispatch(updateUserContactInfo(isCompanyInfo));
      dispatch(setModelSettings(modelSettings));
    }
  }, [userPreferences]);

  useEffect(() => {
    if (licenseLimits?.data) {
      const getPreference = (key: string) =>
        licenseLimits.data.find((item) => item.key === key)?.value;

      const docAnalystAnalysisCount =
        getPreference('doc_analyst_analysis_count') || null;
      let dataAnalystQueryCount =
        getPreference('data_analyst_query_count') || null;
      let searchPlusCount = getPreference('search_plus_count') || null;
      let ConsumedSmartMeetSeconds =
        getPreference('consumed_smart_meet_seconds') || null;

      dispatch(updateDocAnalystAnalysisCount(docAnalystAnalysisCount));
      dispatch(updateDataAnalystQueryCount(dataAnalystQueryCount));
      dispatch(updateSearchPlusCount(searchPlusCount));
      dispatch(updateSmartMeetCount(ConsumedSmartMeetSeconds));
    }
  }, [licenseLimits]);

  useEffect(() => {
    if (configuredModels) {
      const chatModels = configuredModels?.data?.configured_models;
      if (chatModels) {
        let appModels: IAiModel[] = chatModels.map((model: any) =>
          getModelData(model)
        );
        dispatch(updateConfiguredModels(appModels));
      }
      const chatDefaultModel = configuredModels?.data?.chat_default_model;
      if (chatDefaultModel) {
        dispatch(updateChatDefaultModel(getModelData(chatDefaultModel)));
      }
    }
  }, [configuredModels]);

  useEffect(() => {
    if (modelTiers?.data) {
      dispatch(updateModelTiersRateLimit(modelTiers?.data as IModelTierConfig));
    }
  }, [modelTiers]);

  useEffect(() => {
    if (externalApiCredentials) {
      dispatch(
        setExternalApiCredentials({
          kc_token_exchange_authentication_url:
            externalApiCredentials?.kc_token_exchange_authentication_url || '',
          kc_token_exchange_authorization_url:
            externalApiCredentials.kc_token_exchange_authorization_url || '',
          kc_token_exchange_client_id:
            externalApiCredentials?.kc_token_exchange_client_id || '',
          kc_token_exchange_client_secret:
            externalApiCredentials?.kc_token_exchange_client_secret || '',
          is_service_client_created:
            externalApiCredentials?.is_service_client_created || false,
          kc_token_exchange_wps_conn_url:
            externalApiCredentials?.kc_token_exchange_wps_conn_url || '',
          kc_token_exchange_step:
            externalApiCredentials?.kc_token_exchange_step || 0,
          kc_token_exchange_chat_stream:
            externalApiCredentials?.kc_token_exchange_chat_stream,
          kc_token_exchange_status:
            externalApiCredentials?.kc_token_exchange_status,
          kc_token_exchange_token_refresh_rate:
            externalApiCredentials?.kc_token_exchange_token_refresh_rate,
          kc_token_exchange_logout_url:
            externalApiCredentials?.kc_token_exchange_logout_url || ''
        })
      );
    }
    dispatch(setActionsData(Object.values(actions?.data || {}) || []));
  }, [externalApiCredentials, actions]);

  // TODO commented out till finalized design is not provided
  // useEffect(() => {
  //   if (externalApiCredentials) {
  //     dispatch(
  //       setExternalApiCredentials({
  //         kc_realm_name: externalApiCredentials?.kc_realm_name,
  //         kc_token_exchange_client_id:
  //           externalApiCredentials?.kc_token_exchange_client_id || '',
  //         kc_token_exchange_client_secret:
  //           externalApiCredentials?.kc_token_exchange_client_secret || '',
  //         is_service_client_created:
  //           externalApiCredentials?.is_service_client_created || false
  //       })
  //     );
  //   }
  // }, [externalApiCredentials]);

  return { initialize };
}

export default useGlobalData;
